import React from 'react';

import axios from 'axios';
import _ from 'lodash';
import mapboxgl, { LngLat, LngLatBounds, Popup } from 'mapbox-gl';
import { MapContext } from 'react-map-gl';
import { Redirect } from 'react-router-dom';
import { toast } from 'react-toastify';

import MapboxDraw from '@mapbox/mapbox-gl-draw';
import './../Locations/MapLocations.css';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import { Typography } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import AddLocationOutlinedIcon from '@mui/icons-material/AddLocationOutlined';
import CenterFocusWeakOutlinedIcon from '@mui/icons-material/CenterFocusWeakOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import DrawIcon from '@mui/icons-material/Draw';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { Tooltip, Box, IconButton, Button } from '@mui/material';
import * as turf from '@turf/turf';
import { renderToStaticMarkup } from 'react-dom/server';
import { withRouter } from 'react-router-dom/cjs/react-router-dom.min.js';

import { DeleteAllPolygonsPopup } from './DeleteAllPolygonsPopup.js';
import { CustomTooltip, TooltipText } from './MapLocations.styles.js';
import {
  deletePolygonFromLocaleStorage,
  savePolygonsToLocalStorage,
  saveVisibleFeaturesinLocalStorage,
  updatePolygonInLocalStorage,
} from './utils/localStorageActions.js';

import activityZone from '../../assets/images/activity_zone.svg';
import clearCustomAreas from '../../assets/images/clearCustomAreas.svg';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import 'react-toastify/dist/ReactToastify.css';
import { isV2PermissionsEnabled } from '../../helpers/featureFlags/isV2PermissionsEnabled.js';
import { ROLE, hasAnyRole } from '../../utils/permissions.js';
import { protectApiViaOkta } from '../../utils/protectApiViaOkta.js';
import { BASEMAPS, themeOptions } from '../common/constants';
import { APPLICATION_HEADERS, API_URLS } from '../common/headers';
import { providericons } from '../common/providerIconList';
import { toastify } from '../common/Toastify';
import CurrentLocation from '../currentLocation/currentLocation';
import LayerSelector from '../LayerSelector/LayerSelector';
import RequestSurveyBtn from '../RequestSurvey/RequestSurveyBtn';
import { getValueByKeySubstring } from '../Utils/Utils';

mapboxgl.workerClass = require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default;

class MapLocations extends React.Component {
  static contextType = MapContext;

  constructor(props) {
    super(props);
    this.state = {
      map: null,
      showSearch: false,
      addClientBtn: true,
      place_name: null,
      showingBtn: false,
      isModelOpen: false,
      isSurveyModalOpen: false,
      centerData: [],
      polygonData: [],
      providerData: [],
      visibleFeatures: [],
      isRequestButtonEnabled: false,
      isDrawModeEnabled: false,
      isMapPinsError: false,
      activeStyle: BASEMAPS[this.props.URLData.basemap].style,
      theme: BASEMAPS[this.props.URLData.basemap].theme,
      mouseOverTimeout: null,
      drawMode: 'simple_select',
      drawControl: null,
      filterByCustomArea: JSON.parse(localStorage.getItem('filterByCustomArea') || 'false'),
      isDeleteAllPolygonsClicked: false,
      isExpansionModeEnabled: false,
    };
    this.mapContainer = React.createRef();
    this.currentLocationInput = React.createRef(null);
    this.currentAllMarkers;
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.map && prevProps.hoveredProvider !== this.props.hoveredProvider) {
      this.setState(
        {
          visibleFeatures: this.props.hoveredProvider
            ? this.state.providerData.filter(
                (location) =>
                  location.providerName?.toLowerCase() ===
                  this.props.hoveredProvider?.toLowerCase(),
              )
            : this.state.providerData,
        },
        () => {
          this.updateProvidersOnMap();
        },
      );
    }
    if (
      this.state.map &&
      prevState?.drawMode !== this.state.drawMode &&
      this.state.drawMode === 'simple_select'
    ) {
      const hasLocationGeocoder = document.querySelector(
        '#location-geocoder .mapboxgl-ctrl-geocoder--input',
      );

      if (!hasLocationGeocoder) {
        this.addGeocoder();
      }
    }
    if (this.state.map && prevState?.activeStyle !== this.state.activeStyle) {
      this.updateDrawStyles();
    }
    if (prevProps.bingTileUrl === undefined && this.props.bingTileUrl) {
      this.addBingTileLayer();
    }
  }

  componentDidMount() {
    const { lat, lng, zoomlevel } = this.validateUrlMapParams(this.props.URLData);

    const map = new mapboxgl.Map({
      id: 'map',
      container: this.mapContainer.current,
      style: this.state.activeStyle,
      center: [lng, lat],
      zoom: zoomlevel,
      accessToken: APPLICATION_HEADERS.mapBoxAccessToken,
      maxZoom: 15,
      minZoom: 6,
    });
    const popup = new Popup({
      closeButton: false,
      closeOnClick: false,
    });

    map.on('click', () => {
      popup.remove();
    });

    map.on('load', () => {
      const darkMapLayer = this.state.activeStyle === 'dark' || this.state.activeStyle === 'bing';

      const draw = new MapboxDraw({
        displayControlsDefault: false,
        controls: {
          polygon: false,
          trash: false,
        },
        userProperties: true,
        defaultMode: 'simple_select',
        styles: [
          {
            id: 'highlight-active-points',
            type: 'circle',
            filter: [
              'all',
              ['==', '$type', 'Point'],
              ['==', 'meta', 'feature'],
              ['==', 'active', 'true'],
            ],
            paint: {
              'circle-radius': 7,
              'circle-color': '#000000',
            },
          },
          {
            id: 'gl-draw-polygon-and-line-vertex-stroke-inactive',
            type: 'circle',
            filter: [
              'all',
              ['==', 'meta', 'vertex'],
              ['==', '$type', 'Point'],
              ['!=', 'mode', 'static'],
            ],
            paint: {
              'circle-radius': 5,
              'circle-color': '#fff',
              'circle-stroke-color': '#012a2d',
              'circle-stroke-width': 2,
            },
          },
          {
            id: 'gl-draw-polygon-and-line-vertex-inactive',
            type: 'circle',
            filter: [
              'all',
              ['==', 'meta', 'vertex'],
              ['==', '$type', 'Point'],
              ['!=', 'mode', 'static'],
            ],
            paint: {
              'circle-radius': 3,
              'circle-color': '#012a2d',
            },
          },
          {
            id: 'gl-draw-polygon-and-line-vertex-stroke-active',
            type: 'circle',
            filter: [
              'all',
              ['==', 'meta', 'vertex'],
              ['==', '$type', 'Point'],
              ['==', 'active', 'true'],
            ],
            paint: {
              'circle-radius': 7,
              'circle-color': '#fff',
              'circle-stroke-color': '#012a2d',
              'circle-stroke-width': 3,
            },
          },
          {
            id: 'gl-draw-polygon-and-line-vertex-active',
            type: 'circle',
            filter: [
              'all',
              ['==', 'meta', 'vertex'],
              ['==', '$type', 'Point'],
              ['==', 'active', 'true'],
            ],
            paint: {
              'circle-radius': 5,
              'circle-color': '#012a2d',
            },
          },
          {
            id: 'gl-draw-polygon-midpoint',
            type: 'circle',
            filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'midpoint']],
            paint: {
              'circle-radius': 4,
              'circle-color': '#012a2d',
            },
          },
          {
            id: 'gl-draw-line',
            type: 'line',
            filter: ['all', ['==', '$type', 'LineString'], ['!=', 'mode', 'static']],
            layout: {
              'line-cap': 'round',
              'line-join': 'round',
            },
            paint: {
              'line-color': `${darkMapLayer ? '#17e88f' : '#012a2d'}`,
              'line-dasharray': [0.2, 2],
              'line-width': 2,
            },
          },
          {
            id: 'gl-draw-polygon-fill',
            type: 'fill',
            filter: ['all', ['==', '$type', 'Polygon']],
            paint: {
              'fill-color': '#003f2d',
              'fill-outline-color': '#003f2d',
              'fill-opacity': 0.12,
            },
          },
          {
            id: 'gl-draw-polygon-stroke-active',
            type: 'line',
            filter: ['all', ['==', '$type', 'Polygon']],
            layout: {
              'line-cap': 'round',
              'line-join': 'round',
            },
            paint: {
              'line-color': '#003f2d',
              'line-dasharray': [0.2, 2],
              'line-width': 2,
            },
          },
          {
            id: 'gl-draw-polygon-stroke-inactive',
            type: 'line',
            filter: ['all', ['==', 'active', 'false'], ['==', '$type', 'Polygon']],
            paint: {
              'line-color': `${darkMapLayer ? '#17e88f' : '#012a2d'}`,
              'line-width': 2,
            },
          },
        ],
      });

      this.setState({ drawControl: draw });

      map.addControl(draw, 'left');

      const allPolygons = localStorage.getItem('allPolygons');

      if (allPolygons) {
        try {
          const parsedPolygons = JSON.parse(allPolygons);

          if (
            parsedPolygons &&
            parsedPolygons.type === 'FeatureCollection' &&
            Array.isArray(parsedPolygons.features)
          ) {
            draw.add(parsedPolygons);

            this.setState(
              {
                polygonData: parsedPolygons.features
                  .filter((f) => f.geometry?.type === 'Polygon')
                  .map((f) => ({
                    id: f.id,
                    coordinates: f.geometry.coordinates[0],
                  })),
              },
              () => {
                if (this.state.filterByCustomArea && this.state.providerData.length > 0) {
                  const visibleFeatures = this.filterFeaturesByPoligon(this.state.providerData);

                  this.setState({ visibleFeatures }, () => this.updateProvidersOnMap());
                }
              },
            );
          }
        } catch (error) {
          console.error('Error parsing polygons from localStorage:', error);
        }
      }

      map.on('draw.create', () => {
        const { drawControl } = this.state;

        if (!drawControl) return;

        const allFeatures = drawControl.getAll().features;

        if (allFeatures.length === 0) return;

        const existingPolygonIds = this.state.polygonData.map((p) => p.id);
        const newPolygons = allFeatures
          .filter((feature) => !existingPolygonIds.includes(feature.id))
          .map((feature) => ({
            id: feature.id,
            coordinates: feature.geometry.coordinates[0],
          }));

        savePolygonsToLocalStorage(this.state.drawControl);

        this.setState((prevState) => ({
          polygonData: [...prevState.polygonData, ...newPolygons],
        }));
      });

      map.on('draw.update', (e) => {
        const updatedPolygonId = e.features[0].id;
        const updatedPolygonCoordinates = e.features[0].geometry.coordinates[0];

        updatePolygonInLocalStorage(e.features[0], updatedPolygonId, updatedPolygonCoordinates);

        this.setState(
          (prevState) => {
            const updatedPolygonData = prevState.polygonData.map((polygon) =>
              polygon.id === updatedPolygonId
                ? { ...polygon, id: polygon.id, coordinates: updatedPolygonCoordinates }
                : polygon,
            );

            const shouldFilterByArea =
              prevState.filterByCustomArea && updatedPolygonData.length > 0;

            const visibleFeatures = shouldFilterByArea
              ? this.filterFeaturesByPoligon(prevState.providerData, updatedPolygonData)
              : prevState.providerData;

            return {
              polygonData: updatedPolygonData,
              visibleFeatures,
            };
          },
          () => {
            this.updateProvidersOnMap();
          },
        );
      });

      map.on('draw.selectionchange', (e) => {
        const hasSelection = e.features.length > 0;
        const { drawControl } = this.state;
        const currentMode = drawControl ? drawControl.getMode() : 'simple_select';

        this.setState({
          drawMode: hasSelection ? 'select_polygon' : 'simple_select',
          isExpansionModeEnabled: currentMode === 'direct_select',
        });
      });

      const clearPopupRemoveTimeout = () => {
        clearTimeout(this.state.mouseOverTimeout);
        this.setState({ mouseOverTimeout: null });
      };

      const scheduleHoveredPopupRemove = () => {
        clearTimeout(this.state.mouseOverTimeout);

        const timeout = setTimeout(() => {
          popup.remove();
          this.updateProvidersOnMap();
          this.setState({ mouseOverTimeout: null });
        }, 7000);

        this.setState({ mouseOverTimeout: timeout });
      };

      const openPopup = (e) => {
        const coordinates = e.features[0].geometry.coordinates.slice();
        const description = e.features[0].properties.description;

        popup.setLngLat(coordinates).setHTML(description).addTo(map);

        this.updateProvidersOnMap(
          this.state.visibleFeatures.map((feature) =>
            feature.properties.id === e.features[0].properties.id
              ? {
                  ...feature,
                  properties: {
                    ...feature.properties,
                    icon: `${feature.properties.icon}-selected`,
                  },
                }
              : feature,
          ),
        );

        document.querySelector('#closePopup').addEventListener('click', (ev) => {
          clearPopupRemoveTimeout();
          popup.remove();
          this.updateProvidersOnMap();
        });
      };

      this.addProviderIconsToMap();
      map.on('click', 'point', (e) => {
        clearPopupRemoveTimeout();
        openPopup(e);

        const { drawControl } = this.state;

        if (drawControl && typeof drawControl.changeMode === 'function') {
          try {
            // Clear any polygon selection that Draw may have applied
            drawControl.changeMode('simple_select', { featureIds: [] });
          } catch (err) {
            drawControl.changeMode('simple_select');
          }
        }
      });

      map.on('mouseenter', 'point', () => {
        map.getCanvas().style.cursor = 'pointer';
      });

      map.on('mouseover', 'point', (e) => {
        const drawMode = this.state.drawControl.getMode();

        if (drawMode === 'draw_polygon' || drawMode === 'direct_select') {
          return;
        }

        openPopup(e);
        scheduleHoveredPopupRemove();
      });

      map.on('mouseleave', 'point', (e) => {
        map.getCanvas().style.cursor = '';
      });
      if (
        sessionStorage.getItem('CURRENT_LOCATION') &&
        sessionStorage.getItem('CURRENT_LOCATION') !== '' &&
        sessionStorage.getItem('CURRENT_LOC_COORDINATES')
      ) {
        const coordinates = sessionStorage.getItem('CURRENT_LOC_COORDINATES').split(',');

        this.displayMarker(coordinates[0], coordinates[1]);
      }
    });

    map.on('style.load', () => {
      this.addProviderIconsToMap();
      map.addSource('single-point', {
        type: 'geojson',
        generateId: true,
        data: {
          type: 'FeatureCollection',
          features: this.state.visibleFeatures || [],
        },
      });
      map.addLayer({
        id: 'point',
        source: 'single-point',

        type: 'symbol',
        layout: {
          'icon-image': ['get', 'icon'],
          'icon-allow-overlap': true,
          'icon-size': 1,
        },
      });
      if (this.props.bingTileUrl) {
        this.addBingTileLayer();
      }
      this.updateDrawStyles();
    });

    map.on('zoomend', (e) => {
      this.getMapData();
    });

    map.on('dragend', this.getMapData);

    this.setState({ map, activeStyle: this.props.URLData.basemap }, () => {
      this.getMapData();
      this.addGeocoder();
    });
  }

  filterFeaturesByPoligon = (features, polygonDataOverride) => {
    const polygonData = polygonDataOverride || this.state.polygonData;

    if (polygonData.length === 0) {
      return features;
    }

    const allFilteredFeatures = polygonData
      .map((polygon) => {
        const polygonCoordinates = turf.polygon([polygon.coordinates]);
        const points = turf.points(features.map((feature) => feature.geometry.coordinates));
        const pointsWithinPolygon = turf.pointsWithinPolygon(points, polygonCoordinates);

        return pointsWithinPolygon.features
          .map((point) => {
            return features.find(
              (feature) =>
                feature.geometry.coordinates[0] === point.geometry.coordinates[0] &&
                feature.geometry.coordinates[1] === point.geometry.coordinates[1],
            );
          })
          .filter(Boolean);
      })
      .flat();

    const uniqueFeatures = allFilteredFeatures.filter((feature, index, self) => {
      return (
        index ===
        self.findIndex(
          (f) =>
            f.geometry.coordinates[0] === feature.geometry.coordinates[0] &&
            f.geometry.coordinates[1] === feature.geometry.coordinates[1],
        )
      );
    });

    return uniqueFeatures;
  };

  shouldComponentUpdate(nextProps, nextState) {
    return nextProps !== this.props || nextState !== this.state;
  }

  addBingTileLayer = () => {
    if (this.state.map.getSource('bing-tiles') === undefined) {
      this.state.map.addSource('bing-tiles', {
        type: 'raster',
        tiles: [this.props.bingTileUrl || ''],
        tileSize: 256,
      });

      this.state.map.addLayer(
        {
          id: 'bing',
          source: 'bing-tiles',
          type: 'raster',
          minzoom: 0,
          maxzoom: 22,
          paint: {
            'raster-opacity': 1,
          },
          layout: { visibility: this.state.activeStyle === 'bing' ? 'visible' : 'none' },
        },
        'waterway-label',
      );
    }
  };

  addProviderIconsToMap = () => {
    const { map } = this.state;

    providericons.forEach((icon) => {
      if (icon.img && !map.hasImage(`img-${icon.name}`)) {
        const img = new Image(20, 20);
        const imgSelected = new Image(24, 24);

        img.src = icon.img;
        imgSelected.src = icon.imgSelected;
        img.onload = () => map.addImage(`img-${icon.name}`, img);
        imgSelected.onload = () => map.addImage(`img-${icon.name}-selected`, imgSelected);
      }
    });
  };

  updateDrawStyles = () => {
    const { map, activeStyle } = this.state;

    if (!map) return;

    const darkMapLayer = activeStyle === 'dark' || activeStyle === 'bing';
    const lineColor = darkMapLayer ? '#17e88f' : '#012a2d';
    const circleColor = darkMapLayer ? '#17e88f' : '#012a2d';
    const opacity = darkMapLayer ? 0.2 : 0.12;

    const applyStyles = () => {
      const style = map.getStyle();

      if (!style || !style.layers) return;

      style.layers
        .filter((layer) => layer.id.includes('gl-draw') && layer.type === 'line')
        .forEach((layer) => {
          try {
            map.setPaintProperty(layer.id, 'line-color', lineColor);
          } catch (e) {
            // ignore if layer is missing or not yet ready
          }
        });

      style.layers
        .filter((layer) => layer.id.includes('gl-draw') && layer.type === 'fill')
        .forEach((layer) => {
          try {
            map.setPaintProperty(layer.id, 'fill-opacity', opacity);
          } catch (e) {
            // ignore if layer is missing or not yet ready
          }
        });

      style.layers
        .filter((layer) => layer.id.includes('gl-draw') && layer.type === 'circle')
        .forEach((layer) => {
          try {
            map.setPaintProperty(layer.id, 'circle-color', circleColor);
          } catch (e) {
            // ignore if layer is missing or not yet ready
          }
        });
    };

    map.once('idle', applyStyles);
  };

  displayMarker = (lng, lat) => {
    const el = document.createElement('div');

    el.className = 'marker';
    if (this.state.map) {
      new mapboxgl.Marker(el).setLngLat([lng, lat]).addTo(this.state.map);
    }
  };

  addGeocoder = () => {
    const { map } = this.state;

    if (!map) return;

    const locationContainer = document.getElementById('location-geocoder');
    const leftBarContainer = document.getElementById('left-bar-geocoder');

    const hasLocationGeocoder = !!locationContainer?.querySelector('.mapboxgl-ctrl-geocoder');
    const hasLeftBarGeocoder = !!leftBarContainer?.querySelector('.mapboxgl-ctrl-geocoder');

    if (!hasLocationGeocoder && locationContainer) {
      const geocoder = new MapboxGeocoder({
        accessToken: mapboxgl.accessToken,
        mapboxgl: mapboxgl,
        marker: false,
        placeholder: 'Search for an address',
      });

      locationContainer.appendChild(geocoder.onAdd(map));

      geocoder.on('result', async (ev) => {
        const coordinates = ev.result.center;

        this.displayMarker(coordinates[0], coordinates[1]);
        const locationSearch = document.getElementById('location-geocoder');

        if (locationSearch) {
          locationSearch.style.display = 'none';
        }
        sessionStorage.setItem('CURRENT_LOC_COORDINATES', [
          map.getCenter().lng,
          map.getCenter().lat,
        ]);
        sessionStorage.setItem('CURRENT_LOCATION', ev.result.place_name);
      });
    }

    if (!hasLeftBarGeocoder && leftBarContainer) {
      const leftBarGeocoder = new MapboxGeocoder({
        accessToken: mapboxgl.accessToken,
        mapboxgl: mapboxgl,
        marker: false,
        placeholder: 'Search for city',
        collapsed: true,
      });

      leftBarContainer.appendChild(leftBarGeocoder.onAdd(map));

      const self = this;

      leftBarGeocoder.on('result', async function (ev) {
        document.querySelector('#left-bar-geocoder .mapboxgl-ctrl-geocoder--input')?.blur();
        self.props.searchdata(ev);
        if (ev.result && ev.result.bbox) {
          const southWest = new LngLat(ev.result.bbox[0], ev.result.bbox[1]);
          const northEast = new LngLat(ev.result.bbox[2], ev.result.bbox[3]);
          const boundingBox = new LngLatBounds(southWest, northEast);

          map.fitBounds(boundingBox);
        } else if (ev.result && ev.result.center) {
          map.flyTo({ center: ev.result.center, zoom: 11 });
        }
      });
    }
  };

  validateUrlMapParams = ({ lat, lng, zoomlevel }) => {
    const params = { lat, lng, zoomlevel };
    const showError = () => {
      return toastify({
        title: 'Invalid coordinates',
        type: 'error',
        position: toast.POSITION.BOTTOM_RIGHT,
        hideProgressBar: true,
      });
    };

    if (lng < -180 || lng > 180 || isNaN(lng)) {
      showError();
      params.lng = -78.549668;
    }
    if (lat < -90 || lat > 90 || isNaN(lat)) {
      showError();
      params.lat = -78.549668;
    }
    if (isNaN(zoomlevel)) {
      showError();
      params.zoomlevel = 7;
    }

    return params;
  };

  getMapData = async () => {
    const { map } = this.state;

    if (map) {
      const providers = await this.getLocations();

      const features = this.formatLocationData(providers?.data || []);
      const zoomLevel = map.getZoom();

      this.setState(
        {
          providerData: features,
          visibleFeatures:
            this.state.filterByCustomArea && this.state.polygonData.length > 0
              ? this.filterFeaturesByPoligon(features)
              : features,
          isRequestButtonEnabled: zoomLevel > 10 || features.length <= 20,
          isDrawModeEnabled: zoomLevel > 10 || features.length <= 20,
        },
        async () => {
          this.updateProvidersOnMap();
          await this.setPriceAndCenterData();
        },
      );
    }
  };

  updateProvidersOnMap = () => {
    const { map, visibleFeatures } = this.state;

    if (!map || !visibleFeatures) return;

    const finalObject = {
      type: 'FeatureCollection',
      features: visibleFeatures,
    };

    if (finalObject.features.length === 0) {
      this.props.isloaded();
    }
    if (map.getLayer('point') && map.getSource('single-point')) {
      map.getSource('single-point').setData(finalObject);
    }

    saveVisibleFeaturesinLocalStorage(visibleFeatures);
  };

  getLocations = async () => {
    const bounds = this.state.map?.getBounds();
    const sw = bounds.getSouthWest().wrap().toArray();
    const ne = bounds.getNorthEast().wrap().toArray();
    const bbox = sw.concat(ne);

    sessionStorage.setItem('BBOX', bbox);
    const locationObj = {
      method: 'box',
      lower_left: {
        Longitude: bbox[0],
        Latitude: bbox[1],
      },
      upper_right: {
        Longitude: bbox[2],
        Latitude: bbox[3],
      },
      mode: 'noslash',
    };

    this.props.priceData([]);
    this.props.pData([]);
    const url = API_URLS.providerLocationsURL;

    try {
      const response = await protectApiViaOkta('post', url, locationObj);

      this.props.pData(response);

      return response;
    } catch (error) {
      return null;
    }
  };

  getProviderCounts = (data) => {
    const providerAliases = {
      WEWORK: 'WeWork',
      NOVEL: 'Expansive',
      'NOVEL COWORKING': 'Expansive',
    };

    return (data || []).reduce((acc, marker) => {
      const providerName =
        providerAliases[marker.provider_name.toUpperCase().trim()] || marker.provider_name;

      return {
        ...acc,
        [providerName]: (acc[providerName] || 0) + 1,
      };
    }, {});
  };

  setPriceNewData = async (city, region, markerPoints) => {
    this.props.priceData([]);

    if (city && typeof city !== 'object') {
      const priceDetailsRes = await this.getPriceDetails(markerPoints);

      this.props.priceData(priceDetailsRes);
      this.props.region(region);
    } else {
      this.props.region(region);
    }
  };

  getPriceDetails = async (markerPoints) => {
    const url = API_URLS.pricingURL;

    try {
      const priceObj = {
        method: 'full',
        records: markerPoints.map((row) => ({
          Latitude: row.geometry.coordinates[1],
          Longitude: row.geometry.coordinates[0],
          Unit: 25,
          Term: 6,
          Provider: row.providerName,
        })),
      };
      const responsePrice = await protectApiViaOkta('post', url, JSON.stringify(priceObj));

      return JSON.stringify(responsePrice);
    } catch (error) {
      return error;
    }
  };

  setPriceAndCenterData = async () => {
    const { map } = this.state;
    const { providerData } = this.state;
    const addressData = await this.getAddress(map.getCenter().lat, map.getCenter().lng);
    const { city, region } = addressData;

    this.setPriceNewData(city, region, providerData);
    this.setState(
      {
        centerData: [map.getCenter().lng, map.getCenter().lat, map.getZoom(), addressData],
      },
      this.updateUrlParams,
    );
    if (this.state.currentSearch === 1) {
      sessionStorage.setItem('CURRENT_LOC_COORDINATES', [map.getCenter().lng, map.getCenter().lat]);
      this.setState({
        currentSearch: 0,
      });
    }
  };

  updateUrlParams = () => {
    const { map, activeStyle } = this.state;

    const newUrl = `/locations/place/${map.getCenter().lng}/${
      map.getCenter().lat
    }/${map.getZoom()}/${activeStyle}`;

    this.props.history?.push(newUrl);
    sessionStorage.setItem('pathNeeded', newUrl);
  };

  formatLocationData = (data) => {
    const providerCounts = this.getProviderCounts(data);

    const locationObj = (data || [])
      .sort((a, b) => providerCounts[b.provider_name] - providerCounts[a.provider_name])
      .map((marker, index) => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: marker.coordinates.split(',').reverse().map(Number),
        },
        providerName: marker.provider_name,
        properties: {
          id: index,
          icon: `img-${
            providericons.find(
              (icon) =>
                icon.name.toLowerCase().replace(/\s/g, '') ===
                marker.provider_name.toLowerCase().replace(/\s/g, ''),
            )?.name || 'other'
          }`,
          description: this.getMarkerPopup(marker),
        },
      }));

    return locationObj;
  };

  getMarkerPopup = (marker) => {
    return renderToStaticMarkup(
      <div className="marker-popup">
        <div className="popup-content">
          <div>
            <Typography variant="body2" className="secondary popup-title">
              {marker.provider_name}
            </Typography>
            <Typography variant="caption" className="popup-provider">
              {marker.formatted_address.split(',')[0]}
            </Typography>
          </div>
          <div className="popup-close">
            <div id="closePopup" className="button text icon">
              <CloseIcon />
            </div>
          </div>
        </div>
      </div>,
    );
  };

  getCorrectRegionCode = (geographicArray) => {
    const correctShortCode = geographicArray.filter(
      (geo) => geo?.short_code?.split('-').length === 1,
    );

    if (!correctShortCode) {
      console.error('No object with correct short_code found');

      return;
    }

    return correctShortCode[0]?.short_code?.toUpperCase() ?? correctShortCode?.text ?? '';
  };
  getGeographicName = (geographicArray, geoName) => {
    const getObjectByGeoName = geographicArray.filter(
      (geo) => geo?.id?.split('.')?.[0] === geoName,
    );

    return getObjectByGeoName;
  };

  getAddress = async (lat, lng) => {
    const url = `${API_URLS.mapBoxURL}${lng},${lat}.json?limit=1&access_token=${APPLICATION_HEADERS.mapBoxAccessToken}&language=en`;
    const response = await axios.get(url);

    let address = {
      city: '',
      region: '',
      state: '',
      country: '',
      cityState: '',
      place_name: 'Invalid center coordinates',
    };

    if (response?.data?.features?.length > 0) {
      let contextObject, propertiesObject;

      const geographicArray = [];

      response.data.features[0].context
        ? (contextObject = _.keyBy(response.data.features[0].context, 'id'))
        : (propertiesObject = response.data.features[0]);

      if (contextObject) {
        geographicArray.push(
          getValueByKeySubstring(contextObject, 'country'),
          getValueByKeySubstring(contextObject, 'region'),
          getValueByKeySubstring(contextObject, 'place'),
        );
      }

      const getRegionCode = () => {
        if (contextObject) {
          return this.getCorrectRegionCode(geographicArray);
        }

        if (!contextObject) {
          return propertiesObject?.properties?.short_code?.toUpperCase() ?? '';
        }
      };

      const regionCode = getRegionCode();

      const placeObject = this.getGeographicName(geographicArray, 'place');
      const countryObject = this.getGeographicName(geographicArray, 'country');
      const regionObject = this.getGeographicName(geographicArray, 'region');

      address = {
        city: placeObject[0]?.text ?? countryObject[0]?.text ?? '',
        state: regionObject[0]?.text ?? '',
        country: countryObject[0]?.text ?? placeObject[0]?.text ?? '',
        region: regionCode,
        place_name: response.data.features[0].place_name ?? '',
      };

      address.cityState = `${address.city} ${address.state}`.trim().replace(' ', ', ');
    }

    return address;
  };

  handleAddClient = (event) => {
    event.preventDefault();
    const listmenu = document.getElementById('addClientBtn');

    if (listmenu) {
      listmenu.style.display = 'none';
    }

    const geocoderInput = document.querySelector(
      '.location-geocoder .mapboxgl-ctrl-geocoder--input',
    );

    if (geocoderInput) {
      geocoderInput.value = '';
    }

    const locationSearch = document.getElementById('location-geocoder');

    if (locationSearch) {
      locationSearch.style.display = 'flex';
    }
  };

  handleStyleUpdate = (basemap) => {
    const { map } = this.state;

    if (map && basemap.id !== this.state.activeStyle) {
      map.setStyle(basemap.style);
      this.setState({ activeStyle: basemap.id, theme: basemap.theme }, this.updateUrlParams);
    }
  };

  toggleProperties = (show) => {
    const { map } = this.state;

    map && map.setLayoutProperty('point', 'visibility', show ? 'visible' : 'none');
  };

  toggleDrawMode = () => {
    const { drawControl } = this.state;

    if (!drawControl) return;

    const currentMode = drawControl.getMode();

    drawControl?.changeMode(currentMode === 'draw_polygon' ? 'simple_select' : 'draw_polygon');

    this.setState({
      drawMode: currentMode === 'draw_polygon' ? 'simple_select' : 'draw_polygon',
    });
  };

  handleApplyButton = () => {
    const { drawControl } = this.state;

    if (!drawControl) return;

    this.setState({ drawMode: 'simple_select' }, () => {
      drawControl.changeMode('simple_select');
    });
  };

  handleCancelButton = () => {
    const { drawControl } = this.state;

    if (!drawControl) return;

    drawControl.trash();

    drawControl.changeMode('simple_select');

    this.setState({ drawMode: 'simple_select' });
  };

  handleExpansionMode = () => {
    const { drawControl, isExpansionModeEnabled } = this.state;

    if (!drawControl) return;

    const selected = drawControl.getSelected();
    const selectedPolygonId = selected.features[0].id;

    if (!isExpansionModeEnabled) {
      drawControl.changeMode('direct_select', {
        featureId: selectedPolygonId,
      });
      this.setState({ isExpansionModeEnabled: true });
    } else {
      drawControl.changeMode('simple_select', { featureIds: [selectedPolygonId] });
      this.setState({ isExpansionModeEnabled: false });
    }
  };

  handleDeleteButton = () => {
    const { drawControl } = this.state;

    if (!drawControl) return;

    const selectedPolygon = drawControl.getSelected();

    if (selectedPolygon) {
      const selectedPolygonId = selectedPolygon.features[0].id;

      deletePolygonFromLocaleStorage(selectedPolygonId);

      drawControl.delete(selectedPolygonId);

      this.setState(
        (prevState) => ({
          polygonData: prevState.polygonData.filter((polygon) => polygon.id !== selectedPolygonId),
          drawMode: 'simple_select',
        }),
        () => {
          drawControl.changeMode('simple_select');
          const filteredFeatures =
            this.state.providerData.length === 0
              ? this.state.providerData
              : this.filterFeaturesByPoligon(this.state.providerData);

          this.setState({ visibleFeatures: filteredFeatures }, () => {
            this.updateProvidersOnMap();
          });
        },
      );
    }
  };

  handleDeleteAllPolygons = () => {
    const { drawControl } = this.state;

    if (!drawControl) return;

    drawControl.deleteAll();
    localStorage.removeItem('allPolygons');
    this.setState({ isDeleteAllPolygonsClicked: false });

    this.setState(
      { polygonData: [], visibleFeatures: this.state.providerData, filterByCustomArea: false },
      () => {
        this.updateProvidersOnMap();
      },
    );
  };

  handleCenterAllPolygons = () => {
    const { drawControl, map } = this.state;

    if (!drawControl || !map) return;

    const allPolygons = drawControl.getAll();

    if (allPolygons.features.length > 0) {
      const bbox = turf.bbox(allPolygons);

      map.fitBounds(bbox, {
        padding: 50,
      });
    }
  };

  handleFilterByPolygons = () => {
    const { providerData, polygonData, filterByCustomArea } = this.state;
    const nextFlag = !filterByCustomArea;
    const visibleFeatures =
      nextFlag && polygonData.length > 0
        ? this.filterFeaturesByPoligon(providerData)
        : providerData;

    localStorage.setItem('filterByCustomArea', JSON.stringify(nextFlag));
    saveVisibleFeaturesinLocalStorage(visibleFeatures);

    this.setState({ visibleFeatures, filterByCustomArea: nextFlag }, () => {
      this.updateProvidersOnMap();
    });
  };

  handleClearPolygonsState = () => {
    this.setState({
      polygonData: [],
    });
  };

  render() {
    const { children } = this.props;
    const { map, isRequestButtonEnabled, isDrawModeEnabled, showingBtn, theme } = this.state;

    if (sessionStorage.getItem('CURRENT_LOCATION') && document.getElementById('addClientBtn')) {
      document.getElementById('addClientBtn').style.display = 'none';
    }

    if (!sessionStorage.getItem('CURRENT_LOCATION')) {
      !this.state.showingBtn && this.setState({ showingBtn: true });
    }

    if (this.currentLocationInput.current !== null) {
      this.currentLocationInput.current.style.display = !showingBtn
        ? 'none !important'
        : 'flex !important';
    }

    let userIsNotAllowed = false;

    if (isV2PermissionsEnabled()) {
      userIsNotAllowed = hasAnyRole([ROLE.BASIC_PROVIDER]);
    }

    const isButtonIsHidden = hasAnyRole([ROLE.CLIENT, ROLE.ADVANCED_PROVIDER]);

    return userIsNotAllowed && isV2PermissionsEnabled() ? (
      <Redirect to="/providerLocations" />
    ) : (
      <div className="mapDisplay">
        <div className="map-container-box" ref={this.mapContainer}>
          {(this.state.drawMode === 'draw_polygon' || this.state.drawMode === 'select_polygon') && (
            <Box className="polygonInfoContainer">
              <Box className="polygonInfo">
                <DrawIcon />
                <Typography className="polygonInfoText">
                  Draw a shape around the area(s) you would like to create a survey for.
                </Typography>
              </Box>

              <Box className="polygonInfoButtons">
                {this.state.drawMode === 'draw_polygon' && (
                  <IconButton
                    sx={{
                      border: '1px solid #2c5e537a',
                      borderRadius: 0,
                    }}
                    onClick={this.handleCancelButton}
                  >
                    <ClearOutlinedIcon sx={{ color: '#2c5e53', width: 20, height: 20 }} />
                  </IconButton>
                )}
                {this.state.drawMode === 'select_polygon' && (
                  <>
                    <IconButton
                      sx={{
                        border: '1px solid #2c5e537a',
                        borderRadius: 0,
                      }}
                      onClick={this.handleDeleteButton}
                    >
                      <DeleteOutlineOutlinedIcon sx={{ color: '#2c5e53', width: 20, height: 20 }} />
                    </IconButton>
                    <IconButton
                      className="cancelButton"
                      onClick={this.handleExpansionMode}
                      sx={{
                        border: '1px solid #2c5e537a',
                        borderRadius: 0,
                        backgroundColor: this.state.isExpansionModeEnabled ? '#2c5e5352' : '',
                      }}
                    >
                      <img src={activityZone} alt="Activity Zone" className="activityZoneImage" />
                    </IconButton>
                    <IconButton
                      className="applyButton"
                      sx={{
                        border: '1px solid #2c5e537a',
                        borderRadius: 0,
                      }}
                      onClick={this.handleApplyButton}
                    >
                      <CheckOutlinedIcon sx={{ color: '#2c5e53', width: 20, height: 20 }} />
                    </IconButton>
                  </>
                )}
              </Box>
            </Box>
          )}
          {this.state.drawMode === 'simple_select' && (
            <>
              {!isButtonIsHidden && (
                <RequestSurveyBtn
                  centerData={this.state.centerData}
                  providerdata={this.state.visibleFeatures}
                  IsRequestButtonEnabled={isRequestButtonEnabled}
                  draw={this.state.drawControl}
                  polygonData={this.state.polygonData}
                  handleClearPolygonsState={this.handleClearPolygonsState}
                  theme={theme}
                />
              )}
              <div className="mapdisplaybuttons">
                <div style={{ zIndex: 1, top: '10px', left: '10px' }}>
                  <LayerSelector
                    onStyleUpdate={this.handleStyleUpdate}
                    toggleProperties={this.toggleProperties}
                    selectedLayer={this.state.activeStyle}
                  />
                </div>
                <div className="rightButtons">
                  <IconButton
                    sx={{
                      border: '1px solid #2c5e537a',
                      borderRadius: 0,
                      background: '#fff',
                      padding: '7px !important',
                      ':hover': {
                        background: '#ededed',
                      },
                    }}
                    onClick={() => {
                      map.setZoom(map.getZoom() + 1);
                    }}
                  >
                    <AddIcon sx={{ color: '#2c5e53' }} />
                  </IconButton>
                  <IconButton
                    sx={{
                      border: '1px solid #2c5e537a',
                      borderRadius: 0,
                      background: '#fff',
                      padding: '7px !important',
                      ':hover': {
                        background: '#ededed',
                      },
                    }}
                    onClick={() => {
                      map.setZoom(map.getZoom() - 1);
                    }}
                  >
                    <RemoveIcon sx={{ color: '#2c5e53' }} />
                  </IconButton>
                  <Box
                    className="addClient"
                    ref={this.currentLocationInput}
                    id="currentLocationInput"
                  >
                    <CustomTooltip
                      title={<TooltipText>Add a client location pin to map</TooltipText>}
                      placement="left"
                    >
                      <IconButton
                        sx={{
                          border: '1px solid #2c5e537a',
                          borderRadius: 0,
                          background: '#fff',
                          padding: '7px !important',
                          ':hover': {
                            background: '#ededed',
                          },
                        }}
                        id="addClientBtn"
                        onClick={this.handleAddClient}
                      >
                        <AddLocationOutlinedIcon sx={{ color: '#2c5e53' }} />
                      </IconButton>
                    </CustomTooltip>
                    <CurrentLocation locData={this.state.locData} />
                    <div id="location-geocoder" className="location-geocoder"></div>
                  </Box>
                  <Box className="polygonButtons">
                    {!isButtonIsHidden && (
                      <CustomTooltip
                        title={<TooltipText>Draw custom area boundaries</TooltipText>}
                        placement="left"
                      >
                        <IconButton
                          sx={{
                            border: '1px solid #2c5e537a',
                            borderRadius: 0,
                            background: '#fff',
                            padding: '7px !important',
                            ':hover': {
                              background: '#ededed',
                            },
                            '&.Mui-disabled': {
                              background: '#e4e4e4 !important',
                            },
                          }}
                          disabled={!isDrawModeEnabled}
                          onClick={this.toggleDrawMode}
                        >
                          <DrawIcon
                            sx={{ color: `${isDrawModeEnabled ? '#2c5e53' : '#1a1a1a66'}` }}
                          />
                        </IconButton>
                      </CustomTooltip>
                    )}
                    {this.state.polygonData && this.state.polygonData.length > 0 && (
                      <>
                        <CustomTooltip
                          title={<TooltipText>Filter by custom area boundaries</TooltipText>}
                          placement="left"
                        >
                          <IconButton
                            sx={{
                              border: '1px solid #2c5e537a',
                              borderRadius: 0,
                              background: `${!this.state.filterByCustomArea ? '#fff' : '#003f2d'}`,
                              padding: '7px !important',
                              ':hover': {
                                background: `${
                                  !this.state.filterByCustomArea ? '#ededed' : '#01523bff'
                                }`,
                              },
                              '&.Mui-disabled': {
                                background: '#1a1a1a1f !important',
                              },
                            }}
                            onClick={this.handleFilterByPolygons}
                            disabled={!localStorage.getItem('allPolygons')}
                          >
                            {!this.state.filterByCustomArea ? (
                              <FilterAltOffOutlinedIcon sx={{ color: '#2c5e53' }} />
                            ) : (
                              <FilterAltOutlinedIcon sx={{ color: '#fff' }} />
                            )}
                          </IconButton>
                        </CustomTooltip>
                        <CustomTooltip
                          title={<TooltipText>Zoom to fit all custom area boundaries</TooltipText>}
                          placement="left"
                        >
                          <IconButton
                            sx={{
                              border: '1px solid #2c5e537a',
                              borderRadius: 0,
                              background: '#fff',
                              padding: '7px !important',
                              ':hover': {
                                background: '#ededed',
                              },
                              '&.Mui-disabled': {
                                background: '#1a1a1a1f !important',
                              },
                            }}
                            onClick={this.handleCenterAllPolygons}
                            disabled={!localStorage.getItem('allPolygons')}
                          >
                            <CenterFocusWeakOutlinedIcon sx={{ color: '#2c5e53' }} />
                          </IconButton>
                        </CustomTooltip>
                        <CustomTooltip
                          title={<TooltipText>Clear custom area boundaries</TooltipText>}
                          placement="left"
                        >
                          <IconButton
                            sx={{
                              border: '1px solid #2c5e537a',
                              borderRadius: 0,
                              background: '#fff',
                              padding: '7px !important',
                              ':hover': {
                                background: '#ededed',
                              },
                              '&.Mui-disabled': {
                                background: '#1a1a1a1f !important',
                              },
                            }}
                            onClick={() => this.setState({ isDeleteAllPolygonsClicked: true })}
                            disabled={!localStorage.getItem('allPolygons')}
                          >
                            <img src={clearCustomAreas} alt="Clear Custom Areas" />
                          </IconButton>
                        </CustomTooltip>
                      </>
                    )}
                    {this.state.isDeleteAllPolygonsClicked && (
                      <DeleteAllPolygonsPopup
                        open={this.state.isDeleteAllPolygonsClicked}
                        onConfirm={this.handleDeleteAllPolygons}
                        onCancel={() => this.setState({ isDeleteAllPolygonsClicked: false })}
                      />
                    )}
                  </Box>
                </div>
              </div>
            </>
          )}
          {map && children}
        </div>
      </div>
    );
  }
}

export default withRouter(MapLocations);
